import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import AIRecommendationsManager from '../../components/admin/AIRecommendationsManager';
import { Search, RefreshCw, UserCheck, BarChart } from 'lucide-react';
import { hasAIRecommendations } from '../../lib/aiRecommendationsService';

const AIRecommendationsAdmin: React.FC = () => {
  const [prospects, setProspects] = useState<any[]>([]);
  const [filteredProspects, setFilteredProspects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProspectId, setSelectedProspectId] = useState<string | null>(null);
  const [filterHasRecommendations, setFilterHasRecommendations] = useState(false);
  const [prospectRecommendationStatus, setProspectRecommendationStatus] = useState<Record<string, boolean>>({});

  // Charger tous les prospects
  useEffect(() => {
    const fetchProspects = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error } = await supabase
          .from('prospects')
          .select('id, prenom, nom, email, site_url, metadata, quiz_fait, token')
          .order('updated_at', { ascending: false })
          .limit(100);

        if (error) throw error;

        setProspects(data || []);
        setFilteredProspects(data || []);
        
        // Vérifier quels prospects ont des recommandations
        const statusMap: Record<string, boolean> = {};
        await Promise.all(
          (data || []).map(async (prospect) => {
            const hasRecs = await hasAIRecommendations(prospect.id);
            statusMap[prospect.id] = hasRecs;
          })
        );
        
        setProspectRecommendationStatus(statusMap);
      } catch (err) {
        console.error('Erreur lors du chargement des prospects:', err);
        setError('Impossible de charger les prospects. Veuillez réessayer.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProspects();
  }, []);

  // Filtrer les prospects en fonction du terme de recherche
  useEffect(() => {
    let filtered = [...prospects];
    
    // Filtrer par terme de recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.prenom?.toLowerCase().includes(term) ||
          p.nom?.toLowerCase().includes(term) ||
          p.email?.toLowerCase().includes(term) ||
          p.metadata?.company?.toLowerCase().includes(term) ||
          p.site_url?.toLowerCase().includes(term)
      );
    }
    
    // Filtrer par présence de recommandations
    if (filterHasRecommendations) {
      filtered = filtered.filter(p => prospectRecommendationStatus[p.id]);
    }
    
    setFilteredProspects(filtered);
  }, [searchTerm, prospects, filterHasRecommendations, prospectRecommendationStatus]);

  // Rafraîchir les données après une mise à jour
  const handleUpdate = async () => {
    // Mettre à jour le statut de recommandation pour le prospect actuel
    if (selectedProspectId) {
      const hasRecs = await hasAIRecommendations(selectedProspectId);
      setProspectRecommendationStatus(prev => ({
        ...prev,
        [selectedProspectId]: hasRecs
      }));
    }
  };

  // Obtenir le nom de l'entreprise du prospect
  const getCompanyName = (prospect: any) => {
    return prospect.metadata?.company || 'Entreprise non spécifiée';
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Gestion des Recommandations AI</h1>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            {error}
          </div>
        )}
        
        <div className="flex flex-col md:flex-row gap-6">
          {/* Liste des prospects */}
          <div className="w-full md:w-1/3 lg:w-1/4">
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Rechercher un prospect..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mb-4 flex items-center">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterHasRecommendations}
                    onChange={() => setFilterHasRecommendations(!filterHasRecommendations)}
                    className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                  />
                  <span className="ml-2 text-sm text-gray-700">Avec recommandations uniquement</span>
                </label>
              </div>
              
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="animate-spin h-8 w-8 text-blue-500" />
                </div>
              ) : filteredProspects.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Aucun prospect trouvé</p>
              ) : (
                <div className="overflow-y-auto max-h-[600px] pr-2">
                  <ul className="space-y-2">
                    {filteredProspects.map((prospect) => (
                      <li 
                        key={prospect.id}
                        onClick={() => setSelectedProspectId(prospect.id)}
                        className={`p-3 rounded-md cursor-pointer transition-colors duration-150 ${
                          selectedProspectId === prospect.id
                            ? 'bg-blue-50 border-l-4 border-blue-500'
                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-800">{prospect.prenom} {prospect.nom}</p>
                            <p className="text-sm text-gray-600">{getCompanyName(prospect)}</p>
                            <p className="text-xs text-gray-500 mt-1">{prospect.email}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            {prospectRecommendationStatus[prospect.id] && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <BarChart size={12} className="mr-1" />
                                Recs
                              </span>
                            )}
                            {prospect.quiz_fait && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                <UserCheck size={12} className="mr-1" />
                                Quiz
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          
          {/* Gestionnaire de recommandations */}
          <div className="w-full md:w-2/3 lg:w-3/4">
            {selectedProspectId ? (
              <AIRecommendationsManager 
                prospectId={selectedProspectId}
                onUpdate={handleUpdate}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <BarChart className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2">Gestion des recommandations AI</h2>
                <p className="text-gray-600 mb-4">
                  Sélectionnez un prospect dans la liste pour gérer ses recommandations personnalisées.
                </p>
                <p className="text-sm text-gray-500">
                  Vous pourrez ajouter, modifier ou supprimer les recommandations qui s'afficheront sur sa page de résultats.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AIRecommendationsAdmin;
